import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar';
import SectionCheckbox from '../../components/SectionCheckbox';
import Quiz from '../../components/Quiz';
import { PromptBox } from '../../components/CopyButton';
import PromptPlayground from '../../components/PromptPlayground';
import StepByStepGuide from '../../components/StepByStepGuide';
import DiscordWidget from '../../components/DiscordWidget';
import '../Dashboard.css';
import './Module.css';

const MODULE_ID = 6;

// Quiz sur l'autonomie et les projets avancés
const quizQuestions = [
  {
    question: "Quel service est recommandé pour déployer gratuitement une app React ?",
    options: [
      "Un serveur dédié OVH",
      "Vercel",
      "Hébergement mutualisé classique",
      "FTP sur un serveur local"
    ],
    correctAnswer: 1,
    explanation: "Vercel offre un déploiement automatique depuis GitHub, un certificat SSL gratuit, et un CDN mondial. Parfait pour les apps React/Next.js et gratuit pour les projets personnels."
  },
  {
    question: "Quelle est la première étape de la méthode de debug ?",
    options: [
      "Demander à Claude immédiatement",
      "Supprimer le code et recommencer",
      "Identifier le symptôme précisément",
      "Chercher sur Google pendant des heures"
    ],
    correctAnswer: 2,
    explanation: "Avant tout, il faut identifier précisément le symptôme : qu'est-ce qui ne marche pas exactement ? C'est la base pour un debug efficace."
  },
  {
    question: "Que permet une API externe comme OpenAI API ?",
    options: [
      "Rien, c'est inutile",
      "Uniquement créer des images",
      "Intégrer ChatGPT dans votre propre application",
      "Remplacer complètement Claude Code"
    ],
    correctAnswer: 2,
    explanation: "L'API OpenAI permet d'intégrer les capacités de ChatGPT directement dans votre app : génération de texte, analyse, traduction, etc. C'est ce qui alimente beaucoup de produits IA."
  },
  {
    question: "Quel est le tarif de référence pour un site web créé avec l'IA en 1 jour ?",
    options: [
      "50-100€",
      "500-2000€",
      "5000-10000€",
      "Gratuit, on ne peut pas facturer"
    ],
    correctAnswer: 1,
    explanation: "Un site web professionnel créé avec l'IA peut se vendre entre 500€ et 2000€. Vos clients paient pour le résultat et votre expertise, pas pour le temps passé."
  },
  {
    question: "Quelle bonne pratique est essentielle pour maintenir un projet ?",
    options: [
      "Ne jamais modifier le code une fois déployé",
      "Versionner avec Git pour sauvegarder chaque modification",
      "Supprimer les anciennes versions pour économiser l'espace",
      "Coder directement en production"
    ],
    correctAnswer: 1,
    explanation: "Git permet de versionner votre code : chaque modification est sauvegardée et réversible. C'est indispensable pour travailler sereinement et collaborer."
  }
];

// Variables pour le Playground de projet final
const playgroundVariables = [
  {
    name: 'project_idea',
    label: 'Votre idée de projet',
    type: 'textarea',
    placeholder: 'Ex: Un outil pour les coachs sportifs qui génère des programmes d\'entraînement personnalisés basés sur les objectifs du client...'
  },
  {
    name: 'target_market',
    label: 'Marché cible',
    type: 'text',
    placeholder: 'Ex: Coachs sportifs indépendants, salles de fitness, préparateurs physiques...'
  },
  {
    name: 'monetization',
    label: 'Modèle de monétisation',
    type: 'select',
    options: ['Abonnement mensuel (SaaS)', 'Paiement unique', 'Freemium', 'Pay-per-use', 'B2B licence']
  },
  {
    name: 'tech_stack',
    label: 'Stack technique souhaitée',
    type: 'select',
    options: ['React + Vercel (recommandé)', 'Next.js + Vercel', 'React + Node.js + Railway', 'Full-stack avec base de données']
  },
  {
    name: 'timeline',
    label: 'Objectif de lancement',
    type: 'select',
    options: ['Ce weekend (MVP rapide)', 'Cette semaine', 'Ce mois-ci', '3 mois (produit complet)']
  }
];

const playgroundTemplate = `🎯 **PROJET FINAL F12IA™**

## L'idée
{{project_idea}}

## Analyse de marché
- **Cible** : {{target_market}}
- **Modèle économique** : {{monetization}}

## Plan technique
- **Stack** : {{tech_stack}}
- **Objectif** : {{timeline}}

---

**PLAN D'ACTION DÉTAILLÉ**

Aide-moi à structurer ce projet avec :

### 1. Validation de l'idée
- Cette idée résout-elle un vrai problème ?
- Qui sont les concurrents et leurs faiblesses ?
- Quelle est ma proposition de valeur unique ?

### 2. Définition du MVP
- Quelles sont les 3-5 fonctionnalités essentielles ?
- Quel parcours utilisateur minimal ?
- Comment valider avec des vrais utilisateurs ?

### 3. Architecture technique
- Structure des fichiers et composants
- Base de données nécessaire ou pas ?
- APIs externes à intégrer ?

### 4. Plan de lancement
- Étapes de développement prioritaires
- Stratégie de déploiement
- Premiers canaux d'acquisition utilisateurs

### 5. Monétisation
- Pricing recommandé pour ce marché
- Stratégie freemium ou directe ?
- Métriques clés à suivre

Donne-moi un plan concret et actionnable.`;

// Guide étape par étape pour déployer sur Vercel
const deploymentGuideSteps = [
  {
    title: "Créer un compte Vercel",
    content: "Rendez-vous sur vercel.com et créez un compte gratuit. Connectez-le à votre compte GitHub pour des déploiements automatiques.",
    tips: ["Utilisez 'Continue with GitHub' pour une connexion plus simple", "Le plan gratuit inclut des déploiements illimités pour les projets personnels"]
  },
  {
    title: "Préparer votre projet",
    content: "Assurez-vous que votre projet est sur GitHub et que le build fonctionne localement.",
    code: `# Vérifier que le build fonctionne
npm run build

# Si vous n'avez pas encore de repo GitHub :
git init
git add .
git commit -m "Initial commit"
git remote add origin https://github.com/username/repo.git
git push -u origin main`,
    checkpoint: "npm run build doit se terminer sans erreurs"
  },
  {
    title: "Importer le projet dans Vercel",
    content: "Dans le dashboard Vercel, cliquez sur 'Add New...' → 'Project'. Sélectionnez votre repo GitHub. Vercel détecte automatiquement que c'est un projet React.",
    tips: ["Laissez les paramètres par défaut pour un projet Create React App standard", "Vercel détecte automatiquement le framework utilisé"]
  },
  {
    title: "Configurer les variables d'environnement",
    content: "Si votre app utilise des API keys ou variables secrètes, ajoutez-les dans Settings → Environment Variables.",
    code: `# Exemple de variables à configurer
REACT_APP_API_KEY=votre_clé_api
REACT_APP_STRIPE_KEY=pk_live_xxx
REACT_APP_BACKEND_URL=https://api.votreapp.com`,
    warning: "Ne commitez JAMAIS vos clés API dans le code ! Utilisez toujours les variables d'environnement."
  },
  {
    title: "Déployer et configurer le domaine",
    content: "Cliquez sur 'Deploy'. Vercel vous donne une URL en .vercel.app. Pour un domaine personnalisé, allez dans Settings → Domains.",
    prompt: `Mon app est déployée sur Vercel à l'adresse xxx.vercel.app.
Je veux connecter mon domaine personnalisé [votredomaine.com].
Guide-moi pour configurer les DNS chez [votre registrar : Namecheap/OVH/Google Domains].`,
    checkpoint: "Votre app est en ligne ! Testez-la sur différents appareils et navigateurs."
  }
];

const Module6 = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { progress, getTotalProgress } = useProgress();
  const navigate = useNavigate();

  if (!isAuthenticated) { navigate('/login'); return null; }

  const handleLogout = () => { logout(); navigate('/'); };
  const totalProgress = progress?.totalProgress || getTotalProgress() || 0;

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/dashboard')}>
          <span className="logo-text">F12IA</span>
          <span className="logo-tm">™</span>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            {user?.firstName?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.firstName || 'Utilisateur'}</span>
            <span className="user-role">Membre PRO</span>
          </div>
        </div>

        <nav className="sidebar-nav-pro">
          <div className="nav-label">MENU</div>
          <div className="nav-item" onClick={() => navigate('/dashboard')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
            <span className="nav-text">Tableau de bord</span>
          </div>

          <div className="nav-label">MODULES</div>
          <div className="nav-item" onClick={() => navigate('/module/1')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg></span>
            <span className="nav-text">1. Introduction IA</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/2')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
            <span className="nav-text">2. ChatGPT</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/3')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></span>
            <span className="nav-text">3. Claude Code</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/4')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg></span>
            <span className="nav-text">4. Automatisation</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/5')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></span>
            <span className="nav-text">5. Créer des Apps</span>
          </div>
          <div className="nav-item active" onClick={() => navigate('/module/6')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></span>
            <span className="nav-text">6. Projets Avancés</span>
          </div>

          <div className="nav-label">COMPTE</div>
          <div className="nav-item" onClick={() => navigate('/profile')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg></span>
            <span className="nav-text">Mon profil</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/resources')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></span>
            <span className="nav-text">Ressources</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/certificate')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M8 14l-2 8 6-3 6 3-2-8"/></svg></span>
            <span className="nav-text">Certificat</span>
          </div>
        </nav>

        <div className="sidebar-progress-pro">
          <div className="progress-header">
            <span className="progress-title">Progression</span>
            <span className="progress-value">{Math.round(totalProgress)}%</span>
          </div>
          <div className="progress-bar-pro">
            <div className="progress-fill-pro" style={{ width: `${totalProgress}%` }} />
          </div>
        </div>

        <div className="sidebar-bottom">
          <div className="nav-item logout-item" onClick={handleLogout}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span>
            <span className="nav-text">Déconnexion</span>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="module-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← Retour aux programmes</button>
          <div className="module-badge">MODULE 6</div>
          <h1>🧩 Devenir autonome avec l'IA</h1>
          <p className="module-subtitle">Ne plus jamais dépendre de développeurs ou de prestataires</p>
          <ProgressBar moduleId={MODULE_ID} />
        </div>

        <div className="module-content">

          {/* Section 1: Introduction */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>Introduction : L'autonomie totale</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="intro" />
            </div>

            <p>
              Vous avez appris à utiliser ChatGPT, Claude Code, à automatiser et créer des apps.
              <strong> Ce dernier module va vous rendre complètement autonome.</strong> Vous saurez gérer n'importe
              quel projet technique sans dépendre de personne.
            </p>

            <div className="highlight-box">
              <strong>🎯 Objectif du module :</strong> Acquérir les compétences et la méthodologie pour
              être 100% autonome dans vos projets numériques, de l'idée à la mise en production.
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">100%</div>
                <div className="stat-label">Autonome sur vos projets</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">0€</div>
                <div className="stat-label">De prestataires externes</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">∞</div>
                <div className="stat-label">Possibilités de business</div>
              </div>
            </div>
          </section>

          {/* Section 2: Écosystème technique */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>1. L'écosystème technique simplifié</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="ecosystem" />
            </div>

            <p>Voici les briques essentielles de tout projet web moderne :</p>

            <div className="cards-grid">
              <div className="info-card">
                <div className="card-icon">🎨</div>
                <h4>Frontend</h4>
                <p>Ce que l'utilisateur voit. React, HTML/CSS. <strong>Claude Code gère ça.</strong></p>
              </div>
              <div className="info-card">
                <div className="card-icon">⚙️</div>
                <h4>Backend</h4>
                <p>La logique serveur. APIs, authentification. <strong>Claude Code gère aussi.</strong></p>
              </div>
              <div className="info-card">
                <div className="card-icon">💾</div>
                <h4>Base de données</h4>
                <p>Stockage des données. <strong>MongoDB Atlas, Supabase</strong> (gratuits).</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🌐</div>
                <h4>Hébergement</h4>
                <p>Où tourne votre app. <strong>Vercel, Railway</strong> (gratuits).</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🔗</div>
                <h4>Domaine</h4>
                <p>L'adresse de votre site. <strong>~10€/an</strong> chez Namecheap.</p>
              </div>
              <div className="info-card">
                <div className="card-icon">💳</div>
                <h4>Paiements</h4>
                <p>Accepter l'argent. <strong>Stripe</strong>, toujours Stripe.</p>
              </div>
            </div>

            <div className="tip-box">
              <div className="tip-icon">💡</div>
              <div className="tip-content">
                <h4>La stack 100% gratuite pour démarrer</h4>
                <p>Vercel (frontend) + Supabase (backend + BDD) + Stripe (paiements) =
                   un SaaS complet sans rien payer avant d'avoir des clients.</p>
              </div>
            </div>
          </section>

          {/* Section 3: Déployer */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>2. Déployer votre projet en ligne</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="deploy" />
            </div>

            <p>Suivez ce guide pour mettre votre app en ligne sur Vercel :</p>

            <StepByStepGuide
              moduleId={MODULE_ID}
              guideId="deploy-vercel"
              title="Déployer sur Vercel en 5 étapes"
              steps={deploymentGuideSteps}
            />

            <h3>Alternatives d'hébergement</h3>
            <div className="info-box">
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr style={{borderBottom: '1px solid rgba(212, 175, 55, 0.3)'}}>
                    <th style={{textAlign: 'left', padding: '10px'}}>Service</th>
                    <th style={{textAlign: 'left', padding: '10px'}}>Idéal pour</th>
                    <th style={{textAlign: 'left', padding: '10px'}}>Gratuit ?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{padding: '10px'}}>Vercel</td>
                    <td style={{padding: '10px'}}>React, Next.js, sites statiques</td>
                    <td style={{padding: '10px'}}>✅ Oui</td>
                  </tr>
                  <tr>
                    <td style={{padding: '10px'}}>Railway</td>
                    <td style={{padding: '10px'}}>Backend Node.js, Python, BDD</td>
                    <td style={{padding: '10px'}}>✅ 5$/mois gratuit</td>
                  </tr>
                  <tr>
                    <td style={{padding: '10px'}}>Render</td>
                    <td style={{padding: '10px'}}>Full-stack, Docker</td>
                    <td style={{padding: '10px'}}>✅ Tier gratuit</td>
                  </tr>
                  <tr>
                    <td style={{padding: '10px'}}>Supabase</td>
                    <td style={{padding: '10px'}}>Backend + BDD + Auth</td>
                    <td style={{padding: '10px'}}>✅ Très généreux</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 4: Maintenir */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>3. Maintenir et faire évoluer votre projet</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="maintain" />
            </div>

            <h3>Les bonnes pratiques essentielles</h3>
            <ul className="module-list check">
              <li><strong>Versionnez avec Git</strong> — Chaque modification est sauvegardée et réversible</li>
              <li><strong>Testez avant de déployer</strong> — Vérifiez en local, puis en production</li>
              <li><strong>Documentez</strong> — Notez ce que fait chaque partie pour vous y retrouver plus tard</li>
              <li><strong>Sauvegardez la BDD</strong> — Export régulier des données critiques</li>
              <li><strong>Monitorez</strong> — Vercel Analytics, Google Analytics pour suivre l'usage</li>
            </ul>

            <h3>Ajouter des fonctionnalités</h3>
            <PromptBox prompt={`Mon projet actuel est hébergé sur GitHub : [lien ou coller le code concerné]

Je veux ajouter cette fonctionnalité :
[Décrivez précisément la nouvelle feature]

Comportement attendu :
1. L'utilisateur [action]
2. Le système [réaction]
3. Résultat : [ce qui se passe]

Modifie le code existant pour intégrer cette feature.
⚠️ Important : Ne casse pas ce qui fonctionne déjà.`} title="Prompt pour ajouter une feature" />
          </section>

          {/* Section 5: Debug */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>4. Résoudre les problèmes seul</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="debug" />
            </div>

            <h3>La méthode de debug en 4 étapes</h3>
            <div className="steps-list">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>Identifier le symptôme</strong>
                  <p>Qu'est-ce qui ne marche pas exactement ? Soyez précis.</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>Reproduire le bug</strong>
                  <p>Quelles étapes exactes mènent au problème ?</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-content">
                  <strong>Isoler la cause</strong>
                  <p>Quel fichier, quelle fonction est responsable ?</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">4</div>
                <div className="step-content">
                  <strong>Demander à Claude avec contexte</strong>
                  <p>Avec les infos précises, il trouve la solution rapidement.</p>
                </div>
              </div>
            </div>

            <h3>Le template de debug ultime</h3>
            <PromptBox prompt={`🐛 J'ai un bug dans mon application.

**Ce qui devrait se passer :**
[Décrivez le comportement attendu]

**Ce qui se passe réellement :**
[Décrivez le comportement actuel]

**Étapes pour reproduire :**
1. [Étape 1]
2. [Étape 2]
3. [Le bug apparaît]

**Message d'erreur (si applicable) :**
\`\`\`
[Copiez-collez le message d'erreur complet]
\`\`\`

**Code concerné :**
\`\`\`javascript
[Collez le code qui pose problème]
\`\`\`

**Ce que j'ai déjà essayé :**
- [Tentative 1]
- [Tentative 2]

Trouve le problème et propose une solution.`} title="Template de debug complet" />

            <div className="warning-box">
              <strong>⚠️ Erreur courante :</strong> Ne demandez pas juste "ça marche pas".
              Plus vous donnez de contexte (erreur exacte, code, étapes), plus Claude peut vous aider efficacement.
            </div>
          </section>

          {/* Section 6: APIs */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>5. Aller plus loin : les APIs externes</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="apis" />
            </div>

            <p>Les APIs permettent à votre app de communiquer avec d'autres services :</p>

            <div className="cards-grid">
              <div className="info-card">
                <div className="card-icon">🤖</div>
                <h4>OpenAI API</h4>
                <p>Intégrer ChatGPT dans votre app (génération de texte, analyse...)</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📧</div>
                <h4>Resend / SendGrid</h4>
                <p>Envoi d'emails transactionnels (confirmations, notifications)</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📱</div>
                <h4>Twilio</h4>
                <p>SMS et appels vocaux automatisés</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📊</div>
                <h4>Google Sheets API</h4>
                <p>Utiliser des spreadsheets comme base de données simple</p>
              </div>
            </div>

            <PromptBox prompt={`Intègre l'API [NOM DU SERVICE] dans mon projet React.

OBJECTIF :
[Ce que je veux faire avec cette API]

FONCTIONNALITÉ :
- Déclencheur : [quand l'API doit être appelée]
- Action : [ce que l'API doit faire]
- Résultat : [ce qui doit s'afficher/se passer]

MA CLÉ API : [sera stockée en variable d'environnement]

Guide-moi pour :
1. Installer les dépendances nécessaires
2. Configurer l'appel API
3. Gérer les erreurs proprement
4. Afficher le résultat dans l'interface`} title="Prompt d'intégration d'API" />
          </section>

          {/* Section 7: Business */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>6. Créer un business autour de vos compétences</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="business" />
            </div>

            <h3>Vos nouvelles opportunités de revenus</h3>
            <div className="cards-grid">
              <div className="info-card">
                <div className="card-icon">💻</div>
                <h4>Vendre des apps/outils</h4>
                <p>SaaS, templates, solutions sur mesure pour un marché spécifique</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🔧</div>
                <h4>Services freelance</h4>
                <p>Création de sites, apps et automatisations pour clients</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🎓</div>
                <h4>Formations</h4>
                <p>Enseignez ce que vous avez appris à d'autres</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🤝</div>
                <h4>Consulting IA</h4>
                <p>Aidez les entreprises à automatiser leurs process</p>
              </div>
            </div>

            <div className="highlight-box">
              <strong>💰 Tarifs de référence du marché</strong>
              <ul style={{marginTop: '10px', marginBottom: '0'}}>
                <li>Site web professionnel : <strong>500 - 2000€</strong></li>
                <li>Application web simple : <strong>1000 - 5000€</strong></li>
                <li>Automatisation Zapier/Make : <strong>200 - 1000€</strong></li>
                <li>SaaS (revenus récurrents) : <strong>50 - 500€/mois/client</strong></li>
                <li>Consulting IA (journée) : <strong>500 - 1500€</strong></li>
              </ul>
            </div>

            <div className="tip-box">
              <div className="tip-icon">💡</div>
              <div className="tip-content">
                <h4>Le secret : nichéz-vous</h4>
                <p>Ne vendez pas "des sites web". Vendez "des sites pour les coachs sportifs" ou
                   "des automatisations pour les agents immobiliers". Plus c'est spécifique, plus c'est facile à vendre.</p>
              </div>
            </div>
          </section>

          {/* Section 8: Projet final */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>7. Votre projet final</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="final" />
            </div>

            <p>Utilisez ce playground pour définir et planifier votre projet de certification :</p>

            <PromptPlayground
              variables={playgroundVariables}
              template={playgroundTemplate}
              title="Planificateur de projet final"
            />

            <div className="exercise-box" style={{marginTop: '30px'}}>
              <h4>🎓 Critères de certification F12IA™</h4>
              <p>Pour valider votre formation, votre projet doit :</p>
              <ul className="module-list check">
                <li>Résoudre un problème réel pour une cible identifiée</li>
                <li>Avoir au moins 3 fonctionnalités fonctionnelles</li>
                <li>Être déployé en ligne et accessible</li>
                <li>Avoir un design professionnel et responsive</li>
                <li>Inclure une page d'explication de la valeur</li>
              </ul>
            </div>
          </section>

          {/* Quiz */}
          <section className="module-section">
            <h2>📝 Quiz de validation</h2>
            <p>Testez vos connaissances sur l'autonomie et les projets avancés. Score minimum : 70%</p>
            <Quiz
              moduleId={MODULE_ID}
              questions={quizQuestions}
            />
          </section>

          {/* Résumé de la formation */}
          <section className="module-section">
            <h2>📋 Résumé de la formation complète</h2>
            <div className="summary-box">
              <ul>
                <li><strong>Module 1</strong> — L'IA nécessite de bonnes instructions pour bien performer</li>
                <li><strong>Module 2</strong> — ChatGPT : méthode CRISP, itération, prompts précis</li>
                <li><strong>Module 3</strong> — Claude Code : décrivez ce que vous voulez, il code</li>
                <li><strong>Module 4</strong> — Automatisation : règle des 3R, Zapier, Make, scripts</li>
                <li><strong>Module 5</strong> — MVP : lancer vite avec le minimum, itérer, monétiser</li>
                <li><strong>Module 6</strong> — Autonomie : déployer, maintenir, évoluer seul</li>
              </ul>
            </div>

            <div className="highlight-box" style={{background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.05) 100%)', borderColor: 'rgba(16, 185, 129, 0.4)'}}>
              <strong>🎉 Félicitations !</strong><br/><br/>
              Vous avez maintenant toutes les clés pour faire coder l'IA à votre place.
              La seule limite, c'est votre imagination. <strong>Lancez-vous, expérimentez, créez !</strong><br/><br/>
              <em>"Ceux qui maîtrisent l'IA aujourd'hui seront les leaders de demain."</em>
            </div>
          </section>

          {/* Certificat */}
          <section className="module-section">
            <h2>🏆 Obtenez votre certificat</h2>
            {totalProgress >= 100 ? (
              <div className="highlight-box" style={{textAlign: 'center'}}>
                <p style={{fontSize: '1.2em', marginBottom: '20px'}}>
                  <strong>Félicitations ! Vous avez complété 100% de la formation.</strong>
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/certificate')}
                  style={{fontSize: '1.1em', padding: '15px 30px'}}
                >
                  🎓 Télécharger mon certificat
                </button>
              </div>
            ) : (
              <div className="info-box">
                <p>Complétez tous les modules et quiz pour débloquer votre certificat F12IA™.</p>
                <p style={{marginTop: '10px'}}>
                  <strong>Progression actuelle : {Math.round(totalProgress)}%</strong>
                </p>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate('/certificate')}
                  style={{marginTop: '15px'}}
                >
                  Voir ma progression détaillée
                </button>
              </div>
            )}
          </section>

          {/* Discord */}
          <DiscordWidget />

          {/* Navigation */}
          <div className="module-navigation">
            <button className="btn btn-secondary" onClick={() => navigate('/module/5')}>← Module précédent</button>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Retour au Dashboard →</button>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Module6;
